import {
  MaximumFractionDigits,
  PrecisionFloat,
  getPrecisionFloatValue,
  getTargetAmount,
} from '@bts-web/utils-formatting';
import { GLOBAL_MAX_TRADE_AMOUNT, TradeControllerVariants } from '../../utils';
import { OrderOperation } from '../../../orders/types';

/**
 * Determines the maximum trade amount based on transaction type and available balances
 */
export const getMaximumTradeAmount = ({
  fiatBalance,
  assetBalance,
  type,
  orderOperation,
  customMaxFiatAmount,
}: {
  fiatBalance: PrecisionFloat;
  assetBalance: PrecisionFloat;
  type: TradeControllerVariants;
  orderOperation?: OrderOperation;
  customMaxFiatAmount?: string;
}): {
  ASSET: PrecisionFloat;
  FIAT: PrecisionFloat;
} => {
  // For sell operations, use actual balances as maximum
  if (
    type === 'sell' ||
    type === 'swap' ||
    type === 'stake' ||
    type === 'unstake' ||
    (type === 'order' && orderOperation !== OrderOperation.BUY)
  ) {
    return {
      ASSET: {
        value: getPrecisionFloatValue(assetBalance),
        precision: MaximumFractionDigits.ASSET,
      },
      FIAT: {
        value: getPrecisionFloatValue(fiatBalance),
        precision: MaximumFractionDigits.FIAT,
      },
    };
  }

  // For buy and savings, use global maximum
  // customMaxFiatAmount is used for FIAT Deposit Buy
  return {
    ASSET: {
      value: GLOBAL_MAX_TRADE_AMOUNT,
      precision: MaximumFractionDigits.ASSET,
    },
    FIAT: {
      value: customMaxFiatAmount ?? GLOBAL_MAX_TRADE_AMOUNT,
      precision: MaximumFractionDigits.FIAT,
    },
  };
};

/**
 * Determines the minimum trade amount based on transaction type and tenant configuration
 */
export const getMinimumTradeAmount = ({
  type,
  price,
  minimumBuyAmount,
  minimumSellAmount,
  minimumStakeableAmount,
  minimumSavingsAmount,
  orderOperation,
}: {
  type: TradeControllerVariants;
  price: PrecisionFloat;
  minimumBuyAmount: PrecisionFloat;
  minimumSellAmount: PrecisionFloat;
  minimumStakeableAmount?: PrecisionFloat;
  minimumSavingsAmount?: number;
  orderOperation?: OrderOperation;
}) => {
  const minBuyAmountInUnits = getTargetAmount({
    sourceAmount: minimumBuyAmount,
    targetType: 'ASSET',
    price,
  });

  if (type === 'buy') {
    return {
      ASSET: minBuyAmountInUnits,
      FIAT: minimumBuyAmount,
    };
  }

  if (type === 'order') {
    if (orderOperation === OrderOperation.SELL) {
      const minSellAmountInUnits = getTargetAmount({
        sourceAmount: minimumSellAmount,
        targetType: 'ASSET',
        price,
      });

      return { ASSET: minSellAmountInUnits, FIAT: minimumSellAmount };
    }

    return { FIAT: minimumBuyAmount };
  }

  if (type === 'savings') {
    const fallbackMinimumSavingsAmount = 10;

    return {
      FIAT: {
        value: String(minimumSavingsAmount ?? fallbackMinimumSavingsAmount),
        precision: 0,
      },
    };
  }

  if (type === 'stake' || type === 'unstake') {
    const minStakeAmountInUnits = getTargetAmount({
      sourceAmount: minimumStakeableAmount ?? { value: '0', precision: 0 },
      targetType: 'ASSET',
      price,
    });

    return {
      ASSET: minStakeAmountInUnits,
    };
  }

  const minSellAmountInUnits = getTargetAmount({
    sourceAmount: minimumSellAmount,
    targetType: 'ASSET',
    price,
  });

  return {
    ASSET: minSellAmountInUnits,
    FIAT: minimumSellAmount,
  };
};
